import type { ReactNode } from 'react';
import { Component, Fragment, type ErrorInfo, type PropsWithChildren } from 'react';

import { logger, type BoxProps } from '@talos/kyoko';
import { ErrorBoundaryDisplay } from './ErrorBoundaryDisplay';

export type ErrorBoundaryProps = PropsWithChildren<
  {
    message?: ReactNode;
    /** Used for testing reload */
    onReload?: () => void;
  } & BoxProps
>;

export class ErrorBoundary extends Component<ErrorBoundaryProps, { hasError: boolean; reloadKey: number }> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, reloadKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#react-error-boundaries-instrumentation
    const renderingError = new Error(error.message);
    renderingError.name = `ReactRenderingError`;
    renderingError.stack = info.componentStack;
    renderingError.cause = error;
    logger.error(error);
  }

  handleReload = () => {
    if (this.state.reloadKey === 0) {
      // This is the first reload attempt, so we will try to remount the component
      this.setState(prev => ({
        hasError: false,
        reloadKey: prev.reloadKey + 1, // force remount
      }));
    } else {
      // This is the second reload attempt, so we will try to refresh the page
      if (this.props.onReload) {
        this.props.onReload();
      } else {
        window.location.reload();
      }
    }
  };

  override render() {
    const { onReload, ...childProps } = this.props;
    if (this.state.hasError) {
      return <ErrorBoundaryDisplay {...childProps} onReload={this.handleReload} />;
    }

    return <Fragment key={this.state.reloadKey}>{this.props.children}</Fragment>;
  }
}
